import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box, Typography, Grid, Container,
  Card, CardContent, Button, Chip, Paper,
  Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VerifiedIcon from '@mui/icons-material/Verified';
import ComputerIcon from '@mui/icons-material/Computer';
import BoltIcon from '@mui/icons-material/Bolt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import SEO, { organizationSchema, breadcrumbSchema, serviceSchema, faqSchema } from '../../seo/SEO';

function SectionLabel({ text, color = '#2563EB' }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, justifyContent: 'center' }}>
      <Box sx={{ width: 24, height: 2, backgroundColor: color, borderRadius: 1 }} />
      <Typography sx={{ color, fontWeight: 600, fontSize: 12, letterSpacing: 2, textTransform: 'uppercase' }}>{text}</Typography>
      <Box sx={{ width: 24, height: 2, backgroundColor: color, borderRadius: 1 }} />
    </Box>
  );
}

/**
 * Renders visible FAQ content AND is the source of truth for the FAQPage
 * JSON-LD passed via faqSchema() — the same Q&A text appears in both,
 * which is required for the schema to be valid/trustworthy (structured
 * data must match visible page content).
 */
function FAQSection({ faqs, color = '#2563EB' }) {
  return (
    <Box sx={{ mb: 9 }}>
      <Box sx={{ textAlign: 'center', mb: 5 }} className="slide-up delay-1">
        <SectionLabel text="Frequently Asked Questions" color={color} />
        <Typography variant="h3" sx={{ fontWeight: 800, color: '#0F172A', fontSize: { xs: 24, md: 32 }, letterSpacing: '-0.5px' }}>
          Common Questions
        </Typography>
      </Box>
      <Box sx={{ maxWidth: 760, mx: 'auto' }}>
        {faqs.map((f, i) => (
          <Accordion key={i} elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 2, mb: 1.5, '&:before': { display: 'none' }, overflow: 'hidden' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#0F172A' }}>{f.q}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography sx={{ fontSize: 14, color: '#64748B', lineHeight: 1.8 }}>{f.a}</Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Box>
  );
}

/* ─────────── CERTIFICATION ─────────── */
const CERTS = [
  { name: 'ISO 9001', desc: 'Quality Management System — the global standard for quality processes.' },
  { name: 'ISO 14001', desc: 'Environmental Management System for sustainable operations.' },
  { name: 'ISO 45001', desc: 'Occupational Health & Safety Management System.' },
  { name: 'BIS / ISI Mark', desc: 'Bureau of Indian Standards certification for product compliance.' },
  { name: 'FSSAI', desc: 'Food Safety and Standards Authority of India license.' },
  { name: 'BEE Star Rating', desc: 'Bureau of Energy Efficiency energy performance labelling.' },
];

const PROCESS = [
  { step: '01', title: 'Initial Assessment', desc: 'We evaluate your current processes and identify gaps against the certification standard.' },
  { step: '02', title: 'Documentation', desc: 'Our team prepares all required documentation, manuals, and SOPs for you.' },
  { step: '03', title: 'Implementation', desc: 'We guide your team through implementing the required systems and processes.' },
  { step: '04', title: 'Internal Audit', desc: 'Pre-certification audit to ensure everything is ready for the official assessment.' },
  { step: '05', title: 'Certification Audit', desc: 'We coordinate with the certifying body and support you through the final audit.' },
];

const CERTIFICATION_FAQS = [
  { q: 'What is ISO certification and does my business need it?', a: 'ISO certification (such as ISO 9001 for quality management) is an internationally recognised standard that demonstrates your business follows consistent, audited processes. Most B2B buyers, government tenders, and export contracts in India either require or strongly prefer ISO-certified vendors.' },
  { q: 'How long does BIS or FSSAI certification take in India?', a: 'Timelines vary by standard and by how ready your documentation already is. A typical BIS or FSSAI application, once documentation is complete, moves through assessment and approval in a matter of weeks rather than days — we give you an exact timeline after the initial assessment step.' },
  { q: 'Do you handle the certification audit for us?', a: "Yes. We prepare your documentation, run a pre-certification internal audit, and coordinate directly with the certifying body so you're fully ready for the official assessment." },
  { q: 'What industries do you support for certification consultancy?', a: 'We work across manufacturing, food and beverage, IT services, and general trading businesses of all sizes, from startups to established enterprises, anywhere in India.' },
];


/**
 * Compact teaser card on the Certification page that links to the full
 * dedicated Scheme I products page (kept short here on purpose — the full
 * list runs to hundreds of products across 60+ categories, so it lives on
 * its own page rather than bloating this one).
 */
function SchemeITeaser() {
  return (
    <Box
      sx={{
        mb: 9, borderRadius: 3, p: { xs: 4, md: 5 },
        background: 'linear-gradient(135deg, #EFF6FF, #DBEAFE)',
        border: '1px solid #BFDBFE',
        display: 'flex', flexDirection: { xs: 'column', md: 'row' },
        alignItems: 'center', justifyContent: 'space-between', gap: 3,
      }}
      className="slide-up delay-1"
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
        <Box sx={{ width: 56, height: 56, borderRadius: 2, backgroundColor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Inventory2Icon sx={{ fontSize: 28, color: '#2563EB' }} />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: 19, color: '#0F172A', mb: 0.5 }}>
            BIS Scheme I — Products Under Compulsory Certification
          </Typography>
          <Typography sx={{ fontSize: 14, color: '#334155', lineHeight: 1.7, maxWidth: 480 }}>
            Search the full list of products covered under the ISI Mark — cement, electrical goods, medical equipment, steel and more — with the exact Indian Standard for each.
          </Typography>
        </Box>
      </Box>
      <Button
        component={Link}
        to="/services/certification/products-under-compulsory-certification"
        variant="contained"
        endIcon={<ArrowForwardIcon />}
        sx={{ backgroundColor: '#1D4ED8', textTransform: 'none', fontWeight: 700, fontSize: 14, px: 3.5, py: 1.4, borderRadius: 2, boxShadow: 'none', flexShrink: 0, '&:hover': { backgroundColor: '#1E40AF' } }}
      >
        Search Products
      </Button>
    </Box>
  );
}

export function Certification() {
  const description = 'ISO, BIS, FSSAI, BEE and more — end-to-end certification consultancy for Indian businesses, from documentation to final certificate.';
  return (
    <Box sx={{ backgroundColor: '#F8FAFC', minHeight: '100vh' }}>
      <SEO
        title="Certification Consultancy | ISO, BIS, FSSAI, BEE — VforU Global"
        description={description}
        path="/services/certification"
        structuredData={[
          organizationSchema(),
          serviceSchema({ name: 'Certification Consultancy', description, path: '/services/certification' }),
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Services', path: null },
            { name: 'Certification Consultancy', path: '/services/certification' },
          ]),
          faqSchema(CERTIFICATION_FAQS),
        ]}
      />

      {/* Hero with image */}
      <Box sx={{ position: 'relative', minHeight: 420, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
        {/* Background image */}
        <Box sx={{
          position: 'absolute', inset: 0,
          backgroundImage: 'url(https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1400&q=80)',
          backgroundSize: 'cover', backgroundPosition: 'center',
          filter: 'brightness(0.25)',
        }} />
        {/* Gradient overlay */}
        <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(5,15,36,0.9) 0%, rgba(29,78,216,0.7) 100%)' }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, py: 8 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box className="slide-up delay-1">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                  <Box sx={{ width: 48, height: 48, borderRadius: 2, background: 'rgba(37,99,235,0.3)', border: '1px solid rgba(37,99,235,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <VerifiedIcon sx={{ fontSize: 26, color: '#93C5FD' }} />
                  </Box>
                  <Chip label="Certification Services" sx={{ backgroundColor: 'rgba(37,99,235,0.2)', color: '#93C5FD', border: '1px solid rgba(37,99,235,0.3)', fontWeight: 500 }} />
                </Box>
                <Typography variant="h2" sx={{ fontWeight: 800, color: '#fff', fontSize: { xs: 30, md: 48 }, letterSpacing: '-0.5px', lineHeight: 1.15, mb: 2 }}>
                  Certification Consultancy
                </Typography>
                <Typography sx={{ color: '#94A3B8', fontSize: 17, lineHeight: 1.8, mb: 4, maxWidth: 480 }}>
                  End-to-end certification support — ISO, BIS, FSSAI, BEE and more. From gap analysis to final certificate, we handle everything.
                </Typography>
                <Button component={Link} to="/contact" variant="contained" endIcon={<ArrowForwardIcon />}
                  sx={{ background: 'linear-gradient(135deg, #2563EB, #1D4ED8)', textTransform: 'none', fontWeight: 600, fontSize: 15, px: 4, py: 1.5, borderRadius: 2, boxShadow: '0 8px 24px rgba(37,99,235,0.4)', '&:hover': { transform: 'translateY(-2px)' }, transition: 'all 0.25s ease' }}>
                  Start Your Certification
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={6} className="slide-right delay-2">
              <Box sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.1)', aspectRatio: '4/3' }}>
                <img
                  src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&q=80"
                  alt="Certification Consultancy"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 9 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }} className="slide-up delay-1">
          <SectionLabel text="Certifications We Handle" />
          <Typography variant="h3" sx={{ fontWeight: 800, color: '#0F172A', fontSize: { xs: 26, md: 36 }, letterSpacing: '-0.5px' }}>All Major Standards Covered</Typography>
        </Box>
        <Grid container spacing={3} sx={{ mb: 9 }}>
          {CERTS.map((cert, i) => (
            <Grid item xs={12} sm={6} md={4} key={cert.name}>
              <Card className={`slide-up delay-${i + 1}`} sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.06)', height: '100%', transition: 'all 0.3s', '&:hover': { transform: 'translateY(-6px)', boxShadow: '0 16px 36px rgba(0,0,0,0.1)' } }}>
                <Box sx={{ height: 3, background: 'linear-gradient(90deg, #2563EB, #60A5FA)' }} />
                <CardContent sx={{ p: 3 }}>
                  <Chip label={cert.name} sx={{ backgroundColor: '#EFF6FF', color: '#2563EB', fontWeight: 700, mb: 2, fontSize: 12 }} />
                  <Typography sx={{ fontSize: 14, color: '#64748B', lineHeight: 1.7 }}>{cert.desc}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <SchemeITeaser />

        <Box sx={{ textAlign: 'center', mb: 6 }} className="slide-up delay-1">
          <SectionLabel text="How It Works" />
          <Typography variant="h3" sx={{ fontWeight: 800, color: '#0F172A', fontSize: { xs: 26, md: 36 }, letterSpacing: '-0.5px' }}>Our 5-Step Process</Typography>
        </Box>
        <Grid container spacing={3} sx={{ mb: 9 }}>
          {PROCESS.map((p, i) => (
            <Grid item xs={12} sm={6} md={4} key={p.step}>
              <Card className={`slide-up delay-${i + 1}`} sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.06)', height: '100%', transition: 'all 0.3s', '&:hover': { transform: 'translateY(-6px)', boxShadow: '0 16px 36px rgba(0,0,0,0.1)' } }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: 40, color: '#EFF6FF', lineHeight: 1, mb: 1.5 }}>{p.step}</Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: 16, color: '#0F172A', mb: 1 }}>{p.title}</Typography>
                  <Typography sx={{ fontSize: 14, color: '#64748B', lineHeight: 1.7 }}>{p.desc}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <FAQSection faqs={CERTIFICATION_FAQS} color="#2563EB" />

        <Box sx={{ background: 'linear-gradient(135deg, #050F24, #1D4ED8)', borderRadius: 4, p: 6, textAlign: 'center', boxShadow: '0 24px 60px rgba(5,15,36,0.2)' }} className="slide-up delay-1">
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#fff', mb: 1.5, letterSpacing: '-0.5px' }}>Ready to get certified?</Typography>
          <Typography sx={{ color: '#93C5FD', mb: 4, fontSize: 16 }}>Free consultation, no obligation. Let's start your certification journey.</Typography>
          <Button component={Link} to="/contact" variant="contained" endIcon={<ArrowForwardIcon />}
            sx={{ background: 'linear-gradient(135deg, #fff, #EFF6FF)', color: '#1D4ED8', textTransform: 'none', fontWeight: 700, fontSize: 15, px: 5, py: 1.5, borderRadius: 2, '&:hover': { transform: 'translateY(-2px)' }, transition: 'all 0.25s ease' }}>
            Start Your Certification
          </Button>
        </Box>
      </Container>
    </Box>
  );
}

/* ─────────── IT SOLUTIONS ─────────── */
const IT_SERVICES = [
  { name: 'Custom Software Development', desc: 'Web and mobile applications built for your specific business processes.' },
  { name: 'Cloud Solutions', desc: 'AWS, Azure, and GCP migration, architecture, and managed services.' },
  { name: 'Cybersecurity', desc: 'Vulnerability assessments, penetration testing, and security consulting.' },
  { name: 'IT Infrastructure', desc: 'Network setup, server management, and hardware procurement.' },
  { name: 'Digital Transformation', desc: 'End-to-end digitisation of your business processes and workflows.' },
  { name: 'IT Support & AMC', desc: 'Annual maintenance contracts for ongoing IT support and monitoring.' },
];

const IT_FAQS = [
  { q: 'Do you build custom software or only implement existing tools?', a: 'Both. We build custom web and mobile applications tailored to your specific processes, and we also implement and integrate established cloud and enterprise tools where that is the faster, more cost-effective route.' },
  { q: 'Which cloud providers do you work with?', a: 'We work across AWS, Azure, and Google Cloud Platform, covering migration, architecture design, and ongoing managed services.' },
  { q: 'Do you offer ongoing IT support after a project is delivered?', a: 'Yes — we offer Annual Maintenance Contracts (AMC) for ongoing IT support, monitoring, and infrastructure management after go-live.' },
  { q: 'Can you help with cybersecurity for an existing system?', a: 'Yes. We run vulnerability assessments and penetration testing on existing infrastructure and applications, and provide a prioritised remediation plan.' },
];

export function ITSolutions() {
  const description = 'Custom software, cloud infrastructure, and cybersecurity solutions built for Indian businesses — from AWS/Azure/GCP migration to digital transformation and IT support.';
  return (
    <Box sx={{ backgroundColor: '#F8FAFC', minHeight: '100vh' }}>
      <SEO
        title="IT Solutions | Custom Software, Cloud & Cybersecurity — VforU Global"
        description={description}
        path="/services/it-solutions"
        structuredData={[
          organizationSchema(),
          serviceSchema({ name: 'IT Solutions', description, path: '/services/it-solutions' }),
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Services', path: null },
            { name: 'IT Solutions', path: '/services/it-solutions' },
          ]),
          faqSchema(IT_FAQS),
        ]}
      />

      {/* Hero with image */}
      <Box sx={{ position: 'relative', minHeight: 420, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
        <Box sx={{
          position: 'absolute', inset: 0,
          backgroundImage: 'url(https://images.unsplash.com/photo-1535378917042-10a22c95931a?w=1400&q=80)',
          backgroundSize: 'cover', backgroundPosition: 'center',
          filter: 'brightness(0.25)',
        }} />
        <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(4,47,46,0.95) 0%, rgba(14,116,144,0.75) 100%)' }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, py: 8 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box className="slide-up delay-1">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                  <Box sx={{ width: 48, height: 48, borderRadius: 2, background: 'rgba(8,145,178,0.3)', border: '1px solid rgba(8,145,178,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ComputerIcon sx={{ fontSize: 26, color: '#67E8F9' }} />
                  </Box>
                  <Chip label="IT Services" sx={{ backgroundColor: 'rgba(8,145,178,0.2)', color: '#67E8F9', border: '1px solid rgba(8,145,178,0.3)', fontWeight: 500 }} />
                </Box>
                <Typography variant="h2" sx={{ fontWeight: 800, color: '#fff', fontSize: { xs: 30, md: 48 }, letterSpacing: '-0.5px', lineHeight: 1.15, mb: 2 }}>
                  IT Solutions
                </Typography>
                <Typography sx={{ color: '#94A3B8', fontSize: 17, lineHeight: 1.8, mb: 4, maxWidth: 480 }}>
                  From custom software to cloud infrastructure — modern technology solutions that drive real, measurable business growth.
                </Typography>
                <Button component={Link} to="/contact" variant="contained" endIcon={<ArrowForwardIcon />}
                  sx={{ background: 'linear-gradient(135deg, #0891B2, #0E7490)', textTransform: 'none', fontWeight: 600, fontSize: 15, px: 4, py: 1.5, borderRadius: 2, boxShadow: '0 8px 24px rgba(8,145,178,0.4)', '&:hover': { transform: 'translateY(-2px)' }, transition: 'all 0.25s ease' }}>
                  Talk to Our IT Team
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={6} className="slide-right delay-2">
              <Box sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.1)', aspectRatio: '4/3' }}>
                <img
                  src="https://images.unsplash.com/photo-1535378917042-10a22c95931a?w=800&q=80"
                  alt="IT Solutions"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 9 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }} className="slide-up delay-1">
          <SectionLabel text="What We Offer" color="#0891B2" />
          <Typography variant="h3" sx={{ fontWeight: 800, color: '#0F172A', fontSize: { xs: 26, md: 36 }, letterSpacing: '-0.5px' }}>Complete IT Services</Typography>
        </Box>
        <Grid container spacing={3} sx={{ mb: 9 }}>
          {IT_SERVICES.map((svc, i) => (
            <Grid item xs={12} sm={6} md={4} key={svc.name}>
              <Card className={`slide-up delay-${i + 1}`} sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.06)', height: '100%', transition: 'all 0.3s', '&:hover': { transform: 'translateY(-6px)', boxShadow: '0 16px 36px rgba(0,0,0,0.1)' } }}>
                <Box sx={{ height: 3, background: 'linear-gradient(90deg, #0891B2, #67E8F9)' }} />
                <CardContent sx={{ p: 3 }}>
                  <Chip label="IT Solutions" size="small" sx={{ backgroundColor: '#ECFEFF', color: '#0891B2', fontWeight: 700, mb: 2 }} />
                  <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#0F172A', mb: 1 }}>{svc.name}</Typography>
                  <Typography sx={{ fontSize: 14, color: '#64748B', lineHeight: 1.7 }}>{svc.desc}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <FAQSection faqs={IT_FAQS} color="#0891B2" />

        <Box sx={{ background: 'linear-gradient(135deg, #042F2E, #0E7490)', borderRadius: 4, p: 6, textAlign: 'center', boxShadow: '0 24px 60px rgba(4,47,46,0.2)' }} className="slide-up delay-1">
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#fff', mb: 1.5, letterSpacing: '-0.5px' }}>Need an IT solution?</Typography>
          <Typography sx={{ color: '#67E8F9', mb: 4, fontSize: 16 }}>Free consultation — let's discuss your technology needs.</Typography>
          <Button component={Link} to="/contact" variant="contained" endIcon={<ArrowForwardIcon />}
            sx={{ background: 'linear-gradient(135deg, #fff, #ECFEFF)', color: '#0E7490', textTransform: 'none', fontWeight: 700, fontSize: 15, px: 5, py: 1.5, borderRadius: 2, '&:hover': { transform: 'translateY(-2px)' }, transition: 'all 0.25s ease' }}>
            Talk to Our IT Team
          </Button>
        </Box>
      </Container>
    </Box>
  );
}

/* ─────────── ENERGY ─────────── */
const ENERGY_SERVICES = [
  { name: 'Energy Audit', desc: "Detailed assessment of your facility's energy consumption with actionable recommendations." },
  { name: 'Solar Power Solutions', desc: 'Design, installation, and commissioning of rooftop and ground-mounted solar systems.' },
  { name: 'BEE Compliance', desc: 'Bureau of Energy Efficiency compliance consulting and star rating certification support.' },
  { name: 'Energy Management System', desc: 'ISO 50001 Energy Management System implementation and certification.' },
  { name: 'Green Building', desc: 'GRIHA and LEED green building rating consulting for sustainable construction.' },
  { name: 'Carbon Footprint', desc: 'Carbon footprint calculation, reporting, and reduction strategy planning.' },
];

const ENERGY_FAQS = [
  { q: 'What does an energy audit actually involve?', a: "Our certified auditors assess your facility's energy consumption in detail — equipment, processes, and utility usage — and deliver a report with specific, actionable recommendations to reduce costs." },
  { q: 'How much can businesses typically save through an energy audit?', a: 'Based on audits we have completed, businesses typically see a 20–40% reduction in energy costs after implementing our recommendations, though actual savings depend on the facility.' },
  { q: 'Do you help with BEE compliance and star rating certification?', a: 'Yes, BEE (Bureau of Energy Efficiency) compliance consulting and star rating certification support is one of our core energy services, alongside solar and ISO 50001 Energy Management System implementation.' },
  { q: 'Do you handle solar installation as well as consulting?', a: 'Yes — we handle design, installation, and commissioning of both rooftop and ground-mounted solar systems, in addition to the energy audit and compliance side.' },
];

export function Energy() {
  const description = 'Energy audits, solar solutions, and BEE compliance strategies that cut energy bills and ensure Indian businesses meet regulatory standards.';
  return (
    <Box sx={{ backgroundColor: '#F8FAFC', minHeight: '100vh' }}>
      <SEO
        title="Energy Consultancy | Energy Audits, Solar & BEE Compliance — VforU Global"
        description={description}
        path="/services/energy"
        structuredData={[
          organizationSchema(),
          serviceSchema({ name: 'Energy Consultancy', description, path: '/services/energy' }),
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Services', path: null },
            { name: 'Energy Consultancy', path: '/services/energy' },
          ]),
          faqSchema(ENERGY_FAQS),
        ]}
      />

      {/* Hero with image */}
      <Box sx={{ position: 'relative', minHeight: 420, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
        <Box sx={{
          position: 'absolute', inset: 0,
          backgroundImage: 'url(https://images.unsplash.com/photo-1466611653911-95081537e5b7?w=1400&q=80)',
          backgroundSize: 'cover', backgroundPosition: 'center',
          filter: 'brightness(0.25)',
        }} />
        <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(67,20,7,0.95) 0%, rgba(180,83,9,0.75) 100%)' }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, py: 8 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box className="slide-up delay-1">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                  <Box sx={{ width: 48, height: 48, borderRadius: 2, background: 'rgba(217,119,6,0.3)', border: '1px solid rgba(217,119,6,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <BoltIcon sx={{ fontSize: 26, color: '#FCD34D' }} />
                  </Box>
                  <Chip label="Energy Services" sx={{ backgroundColor: 'rgba(217,119,6,0.2)', color: '#FCD34D', border: '1px solid rgba(217,119,6,0.3)', fontWeight: 500 }} />
                </Box>
                <Typography variant="h2" sx={{ fontWeight: 800, color: '#fff', fontSize: { xs: 30, md: 48 }, letterSpacing: '-0.5px', lineHeight: 1.15, mb: 2 }}>
                  Energy Consultancy
                </Typography>
                <Typography sx={{ color: '#94A3B8', fontSize: 17, lineHeight: 1.8, mb: 4, maxWidth: 480 }}>
                  Reduce energy costs, achieve BEE compliance, and build a sustainable future. Expert energy audits and solar solutions for your business.
                </Typography>
                <Button component={Link} to="/contact" variant="contained" endIcon={<ArrowForwardIcon />}
                  sx={{ background: 'linear-gradient(135deg, #D97706, #B45309)', textTransform: 'none', fontWeight: 600, fontSize: 15, px: 4, py: 1.5, borderRadius: 2, boxShadow: '0 8px 24px rgba(217,119,6,0.4)', '&:hover': { transform: 'translateY(-2px)' }, transition: 'all 0.25s ease' }}>
                  Book Free Energy Consultation
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={6} className="slide-right delay-2">
              <Box sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.1)', aspectRatio: '4/3' }}>
                <img
                  src="https://images.unsplash.com/photo-1466611653911-95081537e5b7?w=800&q=80"
                  alt="Energy Consultancy"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 9 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }} className="slide-up delay-1">
          <SectionLabel text="Energy Services" color="#D97706" />
          <Typography variant="h3" sx={{ fontWeight: 800, color: '#0F172A', fontSize: { xs: 26, md: 36 }, letterSpacing: '-0.5px' }}>Comprehensive Energy Solutions</Typography>
        </Box>
        <Grid container spacing={3} sx={{ mb: 7 }}>
          {ENERGY_SERVICES.map((svc, i) => (
            <Grid item xs={12} sm={6} md={4} key={svc.name}>
              <Card className={`slide-up delay-${i + 1}`} sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.06)', height: '100%', transition: 'all 0.3s', '&:hover': { transform: 'translateY(-6px)', boxShadow: '0 16px 36px rgba(0,0,0,0.1)' } }}>
                <Box sx={{ height: 3, background: 'linear-gradient(90deg, #D97706, #FCD34D)' }} />
                <CardContent sx={{ p: 3 }}>
                  <Chip label="Energy" size="small" sx={{ backgroundColor: '#FFFBEB', color: '#D97706', fontWeight: 700, mb: 2 }} />
                  <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#0F172A', mb: 1 }}>{svc.name}</Typography>
                  <Typography sx={{ fontSize: 14, color: '#64748B', lineHeight: 1.7 }}>{svc.desc}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={3} sx={{ mb: 9 }}>
          {[
            { value: '20–40%', label: 'Average energy cost reduction', color: '#D97706', bg: '#FFFBEB' },
            { value: '200+', label: 'Energy audits completed', color: '#059669', bg: '#ECFDF5' },
            { value: '5 MW+', label: 'Solar capacity installed', color: '#2563EB', bg: '#EFF6FF' },
          ].map((stat, i) => (
            <Grid item xs={12} md={4} key={stat.label}>
              <Card className={`slide-up delay-${i + 1}`} sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.06)', border: `1px solid ${stat.color}20`, textAlign: 'center', p: 1, transition: 'all 0.3s', '&:hover': { transform: 'translateY(-6px)' } }}>
                <CardContent>
                  <Typography sx={{ fontWeight: 800, fontSize: 42, color: stat.color, letterSpacing: '-1px' }}>{stat.value}</Typography>
                  <Typography sx={{ fontSize: 14, color: '#64748B', fontWeight: 500 }}>{stat.label}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <FAQSection faqs={ENERGY_FAQS} color="#D97706" />

        <Box sx={{ background: 'linear-gradient(135deg, #431407, #B45309)', borderRadius: 4, p: 6, textAlign: 'center', boxShadow: '0 24px 60px rgba(67,20,7,0.2)' }} className="slide-up delay-1">
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#fff', mb: 1.5, letterSpacing: '-0.5px' }}>Cut your energy bills today</Typography>
          <Typography sx={{ color: '#FCD34D', mb: 4, fontSize: 16 }}>Free energy audit scoping call with our certified auditors.</Typography>
          <Button component={Link} to="/contact" variant="contained" endIcon={<ArrowForwardIcon />}
            sx={{ background: 'linear-gradient(135deg, #fff, #FFFBEB)', color: '#B45309', textTransform: 'none', fontWeight: 700, fontSize: 15, px: 5, py: 1.5, borderRadius: 2, '&:hover': { transform: 'translateY(-2px)' }, transition: 'all 0.25s ease' }}>
            Book Free Energy Consultation
          </Button>
        </Box>
      </Container>
    </Box>
  );
}